import { Fragment, type FocusEvent, type ReactNode, type Ref } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { Fieldset } from './Fieldset';
import { Hint } from './Hint';
import { RequiredMarker } from './RequiredMarker';
import { choiceItemId, type ChoiceItem } from './Radios';
import { cx, describedBy } from './cx';

export interface CheckboxesProps {
  name: string;
  idPrefix: string;
  legend: ReactNode;
  legendClassName?: string;
  legendLang?: string;
  hint?: ReactNode;
  hintLang?: string;
  error?: string;
  /** a red asterisk after the label / legend ("(povinné)" for screen readers) */
  required?: boolean;
  items: ChoiceItem[];
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  small?: boolean;
  ariaDisabled?: boolean;
  extraDescribedBy?: string;
  beforeItems?: ReactNode;
  formGroupClassName?: string;
  firstInputRef?: Ref<HTMLInputElement>;
}

/** govuk/components/checkboxes */
export function Checkboxes({
  name,
  idPrefix,
  legend,
  legendClassName,
  legendLang,
  hint,
  hintLang,
  error,
  required,
  items,
  value,
  onChange,
  onBlur,
  small,
  ariaDisabled,
  extraDescribedBy,
  beforeItems,
  formGroupClassName,
  firstInputRef,
}: CheckboxesProps) {
  const hintId = hint ? `${idPrefix}-hint` : undefined;
  const errorId = error ? `${idPrefix}-error` : undefined;
  const isConditional = items.some((i) => i.conditional !== undefined);
  const toggle = (itemValue: string) => {
    if (ariaDisabled) return;
    onChange(value.includes(itemValue) ? value.filter((v) => v !== itemValue) : [...value, itemValue]);
  };
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <Fieldset
        legend={
          <>
            {legend}
            <RequiredMarker required={required} />
          </>
        }
        legendClassName={legendClassName}
        legendLang={legendLang}
        aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
      >
        {hint && (
          <Hint id={hintId} lang={hintLang}>
            {hint}
          </Hint>
        )}
        {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
        {beforeItems}
        <div
          className={cx('govuk-checkboxes', small && 'govuk-checkboxes--small', isConditional && 'govuk-checkboxes--conditional')}
          data-module={isConditional ? 'govuk-checkboxes' : undefined}
        >
          {items.map((item, index) => {
            const id = choiceItemId(idPrefix, index);
            const checked = value.includes(item.value);
            const itemHintId = item.hint ? `${id}-item-hint` : undefined;
            const conditionalId = `conditional-${id}`;
            const revealed = checked && item.conditional !== undefined && item.conditional !== null;
            return (
              <Fragment key={item.value}>
                <div className="govuk-checkboxes__item">
                  <input
                    ref={index === 0 ? firstInputRef : undefined}
                    className="govuk-checkboxes__input"
                    id={id}
                    name={name}
                    type="checkbox"
                    value={item.value}
                    checked={checked}
                    aria-disabled={ariaDisabled ? true : undefined}
                    aria-describedby={itemHintId}
                    onBlur={onBlur}
                    aria-controls={revealed ? conditionalId : undefined}
                    onChange={() => toggle(item.value)}
                    onClick={(e) => {
                      if (ariaDisabled) e.preventDefault();
                    }}
                  />
                  <label className="govuk-label govuk-checkboxes__label" htmlFor={id} lang={item.lang}>
                    {item.label}
                  </label>
                  {item.hint && (
                    <span id={itemHintId} className="govuk-hint govuk-checkboxes__hint">
                      {item.hint}
                    </span>
                  )}
                </div>
                {revealed && (
                  <div className="govuk-checkboxes__conditional" id={conditionalId}>
                    {item.conditional}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </Fieldset>
    </div>
  );
}
